import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { getLogger, httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  AI_INSIGHTS_REDUCER_KEY,
  type AiInsightsState,
  type AiPromptRequestData,
  type AiPromptResponse,
  createAiPromptRequest,
  type PromptConfig,
  type PromptType,
} from './aiInsightsTypes';
import { investments } from './prompts/investments';
import { investmentsWhy } from './prompts/investmentsWhy';
import { spending } from './prompts/spending';

const log = getLogger('flux/ai-insights/aiInsightsSlice');

const insightsEndpoint = '/llm/v1/stateless-responses/';

interface PromptContextData {
  [key: string]: any;
}

const getTickerSymbol = (contextData: PromptContextData | null | undefined): string | null => {
  if (!contextData || typeof contextData !== 'object') return null;
  const symbol = contextData.symbol;
  return typeof symbol === 'string' && symbol.trim().length > 0 ? symbol : null;
};

interface ResetPromptPayload {
  promptId: string;
  defaultPrompt: PromptConfig | null;
}

type State = Pick<RootState, typeof AI_INSIGHTS_REDUCER_KEY>;

const buildPromptConfig = (key: string, config: any, type: PromptType, includeDataKey: string): PromptConfig => ({
  id: key,
  name: key,
  type,
  prompt: config.prompt,
  contextPrefix: config.contextPrefix || '',
  includeData: config[includeDataKey] ?? true,
  schema: config.schema?.schema || {},
  enableWebSearch: config.enableWebSearch ?? false,
});

const parseResponseWithSchema = (responseString: string): any => {
  try {
    const parsed = JSON.parse(responseString);
    return parsed;
  } catch (error) {
    log.error('Failed to parse response with schema:', error);
    return null;
  }
};

// Make AI API request directly
export const makeAiApiRequest = async (requestData: AiPromptRequestData): Promise<AiPromptResponse> => {
  const url = `${getServiceUrl()}${insightsEndpoint}`;
  log.debug('Making AI prompt request to:', url, 'with payload:', requestData);

  try {
    const response = await httpClientInterface.send('POST', url, requestData);
    log.debug('Received AI prompt response:', response.data);
    return response.data as AiPromptResponse;
  } catch (error) {
    log.error('AI API request failed:', error);
    throw error;
  }
};

// Load default prompts from files (fallback)
const loadDefaultPromptsFromFiles = (): Record<string, PromptConfig> => {
  const prompts: Record<string, PromptConfig> = {};

  Object.entries(spending).forEach(([key, config]) => {
    prompts[key] = buildPromptConfig(key, config, 'spending', 'includeTransactions');
  });

  Object.entries(investments).forEach(([key, config]) => {
    prompts[key] = buildPromptConfig(key, config, 'investments', 'includeHoldings');
  });

  Object.entries(investmentsWhy).forEach(([key, config]) => {
    prompts[key] = buildPromptConfig(key, config, 'investmentsWhy', 'includeHoldings');
  });
  return prompts;
};

// Async thunks
const resetPromptToDefault = createAsyncThunk(
  'aiPrompts/resetPromptToDefault',
  async (promptId: string): Promise<ResetPromptPayload> => {
    const defaultPrompts = loadDefaultPromptsFromFiles();
    const defaultPrompt = defaultPrompts[promptId] || null;
    return { promptId, defaultPrompt };
  },
);

const resetSchemaToDefault = createAsyncThunk(
  'aiPrompts/resetSchemaToDefault',
  async (promptId: string): Promise<ResetPromptPayload> => {
    const defaultPrompts = loadDefaultPromptsFromFiles();
    const defaultPrompt = defaultPrompts[promptId] || null;
    return { promptId, defaultPrompt };
  },
);

const runAiPrompt = createAsyncThunk(
  'aiPrompts/runPrompt',
  async (
    { promptId, contextData }: { promptId: string; contextData: PromptContextData },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as State;
      const promptConfig = state[AI_INSIGHTS_REDUCER_KEY].prompts[promptId];

      if (!promptConfig) {
        throw new Error(`Prompt configuration not found for ${promptId}`);
      }

      // Build request data
      const requestData: AiPromptRequestData = createAiPromptRequest({
        userPrompt: { text: promptConfig.prompt },
        agent: 'ai-insights-v2',
        context: {
          text: promptConfig.contextPrefix,
          data: contextData,
        },
        outputSchema: promptConfig.schema,
        options: {
          enableWebSearch: promptConfig.enableWebSearch ?? false,
        },
        cacheKey: promptConfig.type === 'investmentsWhy' ? (getTickerSymbol(contextData) ?? undefined) : undefined,
      });

      // Make direct API call
      const result = await makeAiApiRequest(requestData);

      // Parse response if schema was used
      let parsedData = null;
      if (promptConfig.schema && result.assistantResponse) {
        parsedData = parseResponseWithSchema(result.assistantResponse);
      }

      return {
        promptId,
        assistantResponse: result.assistantResponse,
        parsedData,
      };
    } catch (error) {
      return rejectWithValue({
        promptId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  },
);

// Initial state uses file-based defaults for immediate availability
const initialState: AiInsightsState = {
  prompts: loadDefaultPromptsFromFiles(),
  results: {},
};

const aiInsightsSlice = createSlice({
  name: AI_INSIGHTS_REDUCER_KEY,
  initialState,
  reducers: {
    // Synchronous actions that don't require feature flag access
    updatePrompt: (state, action: PayloadAction<{ id: string; prompt: string }>) => {
      const { id, prompt } = action.payload;
      if (state.prompts[id]) {
        state.prompts[id].prompt = prompt;
      }
    },

    updateContextPrefix: (state, action: PayloadAction<{ id: string; contextPrefix: string }>) => {
      const { id, contextPrefix } = action.payload;
      if (state.prompts[id]) {
        state.prompts[id].contextPrefix = contextPrefix;
      }
    },

    updateSchema: (state, action: PayloadAction<{ id: string; schema: any }>) => {
      const { id, schema } = action.payload;
      if (state.prompts[id]) {
        state.prompts[id].schema = schema;
      }
    },

    setIncludeData: (state, action: PayloadAction<{ id: string; includeData: boolean }>) => {
      const { id, includeData } = action.payload;
      if (state.prompts[id]) {
        state.prompts[id].includeData = includeData;
      }
    },

    clearResult: (state, action: PayloadAction<string>) => {
      delete state.results[action.payload];
    },

    clearResultsForType: (state, action: PayloadAction<PromptType>) => {
      const promptType = action.payload;
      Object.keys(state.results).forEach((promptId) => {
        if (state.prompts[promptId]?.type === promptType) {
          delete state.results[promptId];
        }
      });
    },
  },

  extraReducers: (builder) => {
    builder
      // Reset individual prompt to default
      .addCase(resetPromptToDefault.fulfilled, (state, action) => {
        const { promptId, defaultPrompt } = action.payload;
        if (defaultPrompt) {
          state.prompts[promptId] = { ...defaultPrompt };
          delete state.results[promptId];
        }
      })

      // Reset schema to default
      .addCase(resetSchemaToDefault.fulfilled, (state, action) => {
        const { promptId, defaultPrompt } = action.payload;
        if (defaultPrompt && state.prompts[promptId]) {
          state.prompts[promptId].schema = defaultPrompt.schema;
        }
      })

      // Run AI prompt
      .addCase(runAiPrompt.pending, (state, action) => {
        const { promptId } = action.meta.arg;
        state.results[promptId] = {
          id: promptId,
          data: null,
          parsedData: null,
          isLoading: true,
          error: null,
          lastUpdated: null,
        };
      })
      .addCase(runAiPrompt.fulfilled, (state, action) => {
        const { promptId, assistantResponse, parsedData } = action.payload;
        state.results[promptId] = {
          id: promptId,
          data: assistantResponse,
          parsedData,
          isLoading: false,
          error: null,
          lastUpdated: DateTime.local().toISO(),
        };
      })
      .addCase(runAiPrompt.rejected, (state, action) => {
        const { promptId, error } = action.payload as { promptId: string; error: string };
        state.results[promptId] = {
          id: promptId,
          data: null,
          parsedData: null,
          isLoading: false,
          error,
          lastUpdated: null,
        };
      });
  },
});

export const { updatePrompt, updateContextPrefix, updateSchema, setIncludeData, clearResult, clearResultsForType } =
  aiInsightsSlice.actions;

export {
  aiInsightsSlice,
  //Utils
  loadDefaultPromptsFromFiles,
  resetPromptToDefault,
  resetSchemaToDefault,
  runAiPrompt,
};
